<template>
  <div
    :class="[
      inline ? 'sticker-insights-inline' : 'sticker-insights-bar',
      !inline && { open: isStickerInsights, closed: !isStickerInsights },
    ]"
  >
    <div v-if="!inline" class="d-flex py-3 fs-14 fw-bold">
      <div style="margin: auto 21px auto auto">
        <el-button link circle @click="closeStickerInsights()" size="small">
          <fai type="fal" icon="times" class="size-18 fw-bold" />
        </el-button>
      </div>
    </div>
    <div class="c-insights" :class="{ 'c-insights--inline': inline }">
      <section class="c-insights__section">
        <p class="c-insights__title">Sales</p>
        <h3 class="c-insights__price">
          ${{ formatRevenue(stickerInsights.totalRevenue) }}
        </h3>
      </section>

      <section
        v-if="variantRows.length"
        class="c-insights__section c-insights__section--middle"
      >
        <p class="c-insights__title c-insights__title--part">Sales by variant</p>
        <article
          v-for="variant in variantRows"
          :key="variant.id"
          class="c-insights__row"
        >
          <p class="c-insights__subtitle">{{ formatVariantLabel(variant) }}</p>
          <article class="c-insights__progress">
            <section class="col">
              <el-progress
                color="#1EBEAF"
                :percentage="getBarPercent(variant.unitsSold, variantTotalUnits)"
                :stroke-width="10"
                :show-text="false"
              />
            </section>
            <span class="c-insights__sold">{{ variant.unitsSold }}</span>
          </article>
        </article>
      </section>

      <section
        v-if="showAffiliateSection"
        class="c-insights__section c-insights__section--middle"
      >
        <p class="c-insights__title c-insights__title--part">Sales by affiliates</p>
        <article
          v-for="(row, index) in affiliateRows"
          :key="row.key"
          class="c-insights__row c-insights__row--affiliate"
        >
          <div class="c-insights__affiliate-header">
            <div class="c-insights__affiliate-name">
              <img
                v-if="row.avatar"
                :src="row.avatar"
                :alt="row.label"
                class="c-insights__affiliate-avatar"
              />
              <fai
                v-else-if="!row.isDirect"
                class="c-insights__affiliate-avatar-fallback"
                type="fal"
                icon="user"
              />
              <router-link
                v-if="row.profilePath"
                :to="row.profilePath"
                class="c-insights__subtitle c-insights__subtitle--link"
              >
                {{ row.label }}
              </router-link>
              <p v-else class="c-insights__subtitle">{{ row.label }}</p>
            </div>
            <div class="c-insights__affiliate-stats">
              <span class="c-insights__sold">{{ row.unitsSold }}</span>
              <span class="c-insights__revenue">${{ formatRevenue(row.totalRevenue) }}</span>
            </div>
          </div>
          <article class="c-insights__progress">
            <section class="col">
              <el-progress
                color="#1EBEAF"
                :percentage="getBarPercent(row.unitsSold, affiliateTotalUnits)"
                :stroke-width="10"
                :show-text="false"
              />
            </section>
          </article>
        </article>
      </section>

      <section
        v-if="winners != null && winners.length"
        class="c-insights__section c-insights__section--middle"
      >
        <p class="c-insights__title">Winners</p>
        <ul class="c-insights__winners-list">
          <li
            v-for="winner in winners"
            :key="winner.id"
            class="winner-item"
          >
            <div class="winner-item__header">
              <img
                v-if="winner.participantAvatar"
                :src="winner.participantAvatar"
                alt="Winner"
                class="winner-item__avatar"
              />
              <fai
                v-else
                class="winner-item__avatar-fallback"
                type="fal"
                icon="user"
              />
              <label :for="winner.id" class="winner-item__name">
                {{ winner.participantName }}
              </label>
              <p :id="winner.id" class="winner-date">
                <i>Notified on {{ formatDate(winner.dateUpdated) }}</i>
              </p>
            </div>
            <p class="winner-email">{{ winner.participantEmail }}</p>
          </li>
        </ul>
      </section>

      <section
        v-if="stickerInsights?.causeSummary?.length"
        class="c-insights__section c-insights__section--middle"
      >
        <p class="c-insights__title c-insights__title--part">Donations by cause</p>
        <article v-for="(cause, i) in stickerInsights.causeSummary" :key="i">
          <p class="c-insights__subtitle">{{ cause.key }}</p>
          <article class="c-insights__progress">
            <section class="col">
              <el-progress
                color="#1EBEAF"
                :percentage="getBarPercent(cause.value, stickerInsights.unitsSold)"
                :stroke-width="10"
                :show-text="false"
              />
            </section>
            <span class="c-insights__sold">{{ cause.value }}</span>
          </article>
        </article>
      </section>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';
import {
  getSocialShopInsightsLabel,
  getSocialShopInsightsProfilePath,
  type SocialShopSummaryRow,
} from '@/views/activity/socialShopAttribution';

interface Winner {
  id: string;
  stickerId: string;
  entityId: string;
  participantName: string;
  participantAvatar: string;
  participantEmail: string;
  eligible: boolean;
  dateCreated: string;
  dateUpdated: string;
}

interface StickerVariantInsight {
  id: string;
  title: string;
  cost: number;
  unitsSold: number;
  totalRevenue: number;
}

interface AffiliateDisplayRow {
  key: string;
  label: string;
  avatar?: string | null;
  profilePath?: string | null;
  unitsSold: number;
  totalRevenue: number;
  isDirect?: boolean;
}

export default defineComponent({
  name: 'StickerInsights',
  props: {
    inline: { type: Boolean, default: false },
    isStickerInsights: { type: Boolean, default: false },
    stickerInsights: { type: Object, default: () => ({}) },
    winners: { type: Array as () => Winner[], default: () => [] },
    winnerName: { type: String, default: null },
  },
  emits: ['close-dialog'],
  setup(props, { emit }) {
    function closeStickerInsights() {
      emit('close-dialog');
    }

    function formatDate(date: string): string {
      const options: Intl.DateTimeFormatOptions = {
        year: '2-digit',
        month: 'numeric',
        day: 'numeric',
      };
      return new Date(date).toLocaleDateString('en-US', options);
    }

    function formatRevenue(value: number | string | undefined): string {
      const num = parseFloat(String(value ?? 0));
      if (Number.isNaN(num)) return '0.00';
      return num.toFixed(2);
    }

    function formatVariantLabel(variant: StickerVariantInsight): string {
      const title = variant.title || 'Variant';
      const cost = parseFloat(String(variant.cost ?? 0));
      if (cost > 0) {
        return `${title} - $${cost % 1 === 0 ? cost.toFixed(0) : cost.toFixed(2)}`;
      }
      return title;
    }

    function getBarPercent(value: number, total: number): number {
      if (!total || !value) return 0;
      return parseFloat(((value / total) * 100).toFixed(2));
    }

    const variantRows = computed((): StickerVariantInsight[] => {
      const variants = props.stickerInsights?.varients;
      if (!Array.isArray(variants)) return [];
      return variants.filter((v) => (v.unitsSold ?? 0) > 0);
    });

    const variantTotalUnits = computed(() => (
      variantRows.value.reduce((sum, v) => sum + (v.unitsSold ?? 0), 0)
    ));

    const socialShopRows = computed((): SocialShopSummaryRow[] => (
      props.stickerInsights?.socialShopSummary ?? []
    ));

    const showAffiliateSection = computed(() => socialShopRows.value.length > 0);

    const affiliateRows = computed((): AffiliateDisplayRow[] => {
      const rows: AffiliateDisplayRow[] = socialShopRows.value.map(
        (row: SocialShopSummaryRow, index: number) => ({
          key: `shop-${index}-${row.socialShopEntityId ?? 'withheld'}`,
          label: getSocialShopInsightsLabel(row),
          avatar: row.isSocialShopOwnerShared ? row.socialShopAvatar : null,
          profilePath: getSocialShopInsightsProfilePath(row),
          unitsSold: row.unitsSold ?? 0,
          totalRevenue: row.totalRevenue ?? 0,
        }),
      );

      const direct = props.stickerInsights?.directSalesSummary;
      if (direct && (direct.unitsSold ?? 0) > 0) {
        rows.push({
          key: 'direct',
          label: 'Direct',
          unitsSold: direct.unitsSold ?? 0,
          totalRevenue: direct.totalRevenue ?? 0,
          isDirect: true,
        });
      }

      return rows;
    });

    const affiliateTotalUnits = computed(() => (
      affiliateRows.value.reduce((sum, row) => sum + row.unitsSold, 0)
    ));

    return {
      closeStickerInsights,
      formatDate,
      formatRevenue,
      formatVariantLabel,
      getBarPercent,
      variantRows,
      variantTotalUnits,
      showAffiliateSection,
      affiliateRows,
      affiliateTotalUnits,
    };
  },
});
</script>

<style lang="scss">
.sticker-insights-bar {
  height: 100%;
  width: 0;
  position: fixed;
  z-index: 1;
  top: 0;
  right: 0;
  background-color: white;
  overflow-x: hidden;
  overflow-y: auto;
  transition: 0.5s;
  font-family: "Red Hat Display";

  &.closed {
    width: 0;
  }

  &.open {
    width: 480px;
  }
}

.sticker-insights-inline {
  font-family: "Red Hat Display";
  width: 100%;
}

.c-insights {
  display: flex;
  flex-direction: column;
  font-family: "Red Hat Display";
  padding-bottom: 40px;

  &--inline {
    padding-bottom: 0;

    .c-insights__section {
      padding: 0;
    }

    .c-insights__section--middle {
      padding: 20px 0 0;
      margin: 20px 0 0;
    }

    .c-insights__price {
      font-size: 24px;
      line-height: 1.2;
    }

    .c-insights__progress {
      gap: 10px;
    }

    .c-insights__affiliate-header {
      gap: 8px;
    }
  }

  &__title {
    color: black;
    font-size: 14px;
    line-height: 18px;
    font-weight: 900;
    margin: 0 0 10px;
    text-transform: uppercase;
    letter-spacing: 0.02em;
  }

  &__cause, &__subtitle {
    font-weight: 500;
    font-size: 12px;
    color: black;
    margin: 0;
  }

  &__subtitle {
    margin: 0 0 6px;

    &--link {
      text-decoration: none;
      color: black;

      &:hover {
        color: #1EBEAF;
      }
    }
  }

  &__price {
    font-weight: 700;
    font-size: 30px;
    line-height: 54px;
    margin: 0;
  }

  &__sold {
    font-size: 12px;
    font-weight: 600;
    min-width: 24px;
    text-align: right;
  }

  &__revenue {
    font-size: 12px;
    font-weight: 600;
    color: #666;
  }

  &__progress {
    display: flex;
    gap: 17px;
    align-items: center;
  }

  &__row {
    margin-bottom: 16px;

    &:last-child {
      margin-bottom: 0;
    }

    &--affiliate {
      .c-insights__progress {
        margin-top: 4px;
      }
    }
  }

  &__affiliate-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 12px;
  }

  &__affiliate-name {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  &__affiliate-avatar {
    width: 20px;
    height: 20px;
    border-radius: 50%;
    object-fit: cover;
    flex-shrink: 0;
  }

  &__affiliate-avatar-fallback {
    width: 20px;
    height: 20px;
    flex-shrink: 0;
    color: #999;
  }

  &__affiliate-stats {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    flex-shrink: 0;
    gap: 2px;
  }

  &__winners-list {
    list-style: none;
    padding: 0;
    margin: 0;
  }

  &__section {
    padding: 0 30px;

    &--middle {
      padding: 25px 30px;
      margin: 25px 0 0;
      border-top: 1px solid #EFEFEF;
    }
  }

  .winner-item {
    margin-bottom: 12px;
    padding: 8px;
    border: 1px solid #EFEFEF;
    border-radius: 4px;

    &__header {
      display: flex;
      align-items: center;
      margin-bottom: 4px;
    }

    &__avatar {
      width: 20px;
      height: 20px;
      margin-right: 8px;
      border-radius: 50%;
      object-fit: cover;
    }

    &__avatar-fallback {
      width: 20px;
      height: 20px;
      margin-right: 8px;
      color: #999;
    }

    &__name {
      font-weight: 600;
      font-size: 12px;
    }
  }

  .winner-date {
    margin: 0 0 0 auto;
    color: #999999;
    font-size: 11px;
  }

  .winner-email {
    margin: 0 0 0 28px;
    font-size: 12px;
    color: #666;
  }
}
</style>
